import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { fetchCollections, type ShopifyCollection } from '../utils/shopify'
import { COLLECTIONS } from '../types'
import { useSeo } from '../hooks/useSeo'

// ─── Hero Slides ────────────────────────────────────────────────────────────
const HERO_SLIDES = [
  {
    image: '/banner-hero-1.jpg',
    headline: ['Your space.', 'Your signature.'],
    sub: 'Unique mouse mats designed to transform your desk into a space that reflects who you are.',
    cta: { label: 'DISCOVER OUR COLLECTIONS', href: '/catalogue' },
  },
  // Future slides can be added here
]

// ─── Quick-nav items (icon bar below hero) ──────────────────────────────────
const NAV_ITEMS = [
  { icon: '/icons/mon-compte.svg',          label: 'MY ACCOUNT',    sub: 'Manage your information and preferences',  href: '/account' },
  { icon: '/icons/mon-panier.svg',          label: 'MY CART',       sub: 'View your items and finalise your purchase', href: '/cart' },
  { icon: '/icons/rechercher.svg',          label: 'SEARCH',        sub: 'Find exactly what you are looking for',     href: '/catalogue' },
  { icon: '/icons/mes-commandes.svg',       label: 'MY ORDERS',     sub: 'Track the status of your orders',           href: '/account' },
  { icon: '/icons/liste-de-souhaits.svg',   label: 'WISHLIST',      sub: 'Your saved favourites',                     href: '/account' },
  { icon: '/icons/adresses.svg',            label: 'ADDRESSES',     sub: 'Manage your delivery addresses',            href: '/account' },
  { icon: '/icons/aide-contact.svg',        label: 'HELP & CONTACT',sub: 'We are here to assist you',                 href: '/account' },
]

// ─── Trust badges (bottom strip) ────────────────────────────────────────────
const TRUST_ITEMS = [
  { icon: '/icons/livraison-rapide.svg',    label: 'FAST DELIVERY',    sub: 'Across Canada' },
  { icon: '/icons/materiaux-durables.svg',  label: 'DURABLE MATERIALS',sub: 'Carefully selected' },
  { icon: '/icons/paiements-securises.svg', label: 'SECURE PAYMENTS',  sub: '100% secure transaction' },
]

// ─── Description snippet helper ─────────────────────────────────────────────
function descSnippet(desc: string): string {
  const words = desc.replace(/<[^>]+>/g, '').trim().split(/\s+/)
  return words.slice(0, 3).join(' ')
}

// ─── Collection description map (from seeded types) ─────────────────────────
const COL_DESC_MAP: Record<string, string> = Object.fromEntries(
  COLLECTIONS.map((c) => [c.handle, c.description])
)

export default function HomePage() {
  useSeo({ path: '/' })
  const [collections, setCollections] = useState<ShopifyCollection[]>([])
  const [loading, setLoading] = useState(true)
  const [slide, setSlide] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto-advance hero slider
  useEffect(() => {
    if (HERO_SLIDES.length <= 1) return
    timerRef.current = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])

  const goSlide = (idx: number) => {
    setSlide(idx)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000)
  }

  useEffect(() => {
    fetchCollections()
      .then((cols) => setCollections(cols.filter((c) => c.handle !== 'home-page').slice(0, 15)))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const current = HERO_SLIDES[slide]

  return (
    <div className="min-h-screen bg-brand-dark">

      {/* ── Hero Banner Slider ────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden" style={{ height: 'min(90vh, 680px)' }}>
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === slide ? 1 : 0 }}
          >
            <img
              src={s.image}
              alt=""
              className="w-full h-full object-cover object-center"
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            {/* Dark overlay — left side only */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          </div>
        ))}

        {/* Text content — left aligned */}
        <div className="absolute inset-0 flex items-center">
          <div className="pl-12 md:pl-20 lg:pl-28">
            <div className="max-w-md">
              {/* Logo wordmark */}
              <div className="mb-4">
                <span className="font-serif text-3xl font-light tracking-wide text-white">
                  Matr<span className="text-brand-gold italic">y</span>x<span className="text-white">Lab</span>
                </span>
                <div className="flex items-center gap-3 mt-1">
                  <div className="h-px w-16 bg-brand-gold/60" />
                  <span className="text-brand-gold text-base">✦</span>
                  <div className="h-px w-16 bg-brand-gold/60" />
                </div>
              </div>

              {/* Headline */}
              <h1 className="font-serif text-5xl md:text-6xl font-light text-white leading-tight mb-4">
                {current.headline.map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </h1>

              {/* Sub */}
              <p className="text-white/75 text-base font-light leading-relaxed mb-8 max-w-xs">
                {current.sub}
              </p>

              {/* CTA */}
              <Link to={current.cta.href} className="btn-primary inline-flex items-center gap-3">
                {current.cta.label} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* Slider dots */}
        {HERO_SLIDES.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => goSlide(i)}
                className="transition-all duration-300"
                style={{
                  width: i === slide ? 40 : 24,
                  height: 2,
                  background: i === slide ? '#C8A36A' : 'rgba(200,163,106,0.35)',
                }}
              />
            ))}
          </div>
        )}

        {/* Prev / Next arrows (only when multiple slides) */}
        {HERO_SLIDES.length > 1 && (
          <>
            <button
              onClick={() => goSlide((slide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-white/30 text-white hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => goSlide((slide + 1) % HERO_SLIDES.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-white/30 text-white hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </section>



      {/* ── Collections Grid ─────────────────────────────────────────────── */}
      <section className="py-16 px-4 bg-brand-cream">
        <div className="max-w-7xl mx-auto">

          {/* Section header */}
          <div className="flex flex-col items-center mb-10">
            <div className="gold-divider w-full max-w-xs mb-3">
              <span className="star-ornament">✦</span>
            </div>
            <h2 className="font-serif text-2xl font-light tracking-[0.25em] uppercase text-brand-navy">
              Choose Your Universe
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-brand-gold" size={28} />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {collections.map((col) => {
                const desc = COL_DESC_MAP[col.handle] || col.body_html || ''
                const snippet = descSnippet(desc)
                return (
                  <Link
                    key={col.id}
                    to={`/collections/${col.handle}`}
                    className="group relative overflow-hidden bg-brand-navy rounded-xl"
                    style={{ aspectRatio: '5/2' }}
                  >
                    {/* Background image */}
                    {col.image?.src ? (
                      <img
                        src={col.image.src}
                        alt={col.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy to-brand-surface" />
                    )}

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                    {/* Text */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="font-serif text-white text-sm font-light tracking-widest uppercase leading-tight">
                        {col.title}
                      </h3>
                      {snippet && (
                        <p className="text-white/55 text-[10px] font-light mt-0.5 tracking-wide">
                          {snippet}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-1">
                        <span className="text-brand-gold text-xs">→</span>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── Trust Strip ──────────────────────────────────────────────────── */}
      <section className="bg-brand-cream border-t border-brand-gold/20 py-8 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-brand-gold/20">
          {TRUST_ITEMS.map(({ icon, label, sub }) => (
            <div key={label} className="flex items-center gap-4 justify-center py-2 md:py-0">
              <img src={icon} alt={label} className="w-8 h-8 opacity-60" style={{ filter: 'invert(0.3) sepia(1) saturate(2) hue-rotate(5deg)' }} />
              <div>
                <p className="text-brand-navy text-xs font-medium tracking-widest uppercase">{label}</p>
                <p className="text-brand-navy/60 text-xs font-light mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  )
}
